import type {PaginatedResponses, EventResponse} from 'src/types/report';
// @ts-expect-error - TS2614 - Module '"src/api-parsers/events"' has no exported member 'Event'. Did you mean to use 'import Event from "src/api-parsers/events"' instead?
import type {Event} from 'src/api-parsers/events';
import type {SenseEvent} from 'src/types/events';
import type {Workflow} from 'src/types/workflow';
import type {FlaggedWord} from 'src/action-creators/flagging';
import type {RatingScaleQuestion, NPSSurveyQuestion} from 'src/types/survey';
import type {AtsEntity} from 'src/types/ats-entities';
import useAnalyticsQuery from 'src/components/analytics/useAnalyticsQuery';
import {useReleaseFlag} from 'src/hooks/product-flags';

import * as React from 'react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {Link} from 'src/rerouter';
import {useSelector, useDispatch} from 'react-redux';
import Highlighter from 'src/components/lib/highlighter';

import {classify, getRatingMood, getNpsRatingMood} from 'src/utils';
import {formatDateTime} from 'src/utils/date-time';
import {resolveAnalyticsPath} from 'src/utils/analytics-query';
import {
  allowedFilters,
  resolveResponsesQuery,
} from 'src/action-creators/event-responses';
import {selectEntityMappingsByName} from 'src/selectors/ats-entities';
import makeClassNameComponent, {
  makeClassNameComponentCustom,
  type ClassNameComponent,
} from 'src/utils/makeClassNameComponent';
import {
  checkResponseLatestApiEnabled,
  ANALYTICS_API_V3,
} from 'src/utils/analytics-api-migration';
import {startDataExport} from 'src/action-creators/data-export-report';

import {Button} from 'src/designSystem2021Components/buttons';
import {UnstyledButton} from 'src/components/lib/new-button';
import EventIcon from 'src/components/workflow/event/icon';
import {FormattedPlural} from 'src/components/lib/intl';
import CsvDownloadButton from 'src/components/analytics/figures/csv-download-button';
import MoreButton from 'src/components/analytics/figures/more-button';
import {AutoTruncatedText} from 'src/components/lib/truncated-text/truncated-text';

import CaretDown from 'src/images/designSystems2021/caret-down.svg';

import css from './responses.css';

import {Paragraph, Clickable} from '../../designSystem2021Components/text-v2';
import type {
  Conversation,
  ConversationUploadedfiles,
} from '../../types/chatbot-analytics';


export type ResponsesParams = {
  responseId: string;
  workflowId: string;
  eventId: string;
};

type ResponsesProps = {
  responseData: PaginatedResponses;
  params: ResponsesParams;
  loadingMore: boolean;
  onMoreClick: () => void;
  flagged?: boolean;
  flaggedWords?: FlaggedWord[];
  isNps?: boolean;
  multiEntityEnabled: boolean;
};

const PaginatedResponsesView = ({
  responseData,
  onMoreClick,
  loadingMore,
  params,
  flagged = false,
  flaggedWords = [],
  isNps = false,
  multiEntityEnabled,
}: ResponsesProps) => {
  const {t} = useLabelTranslation();
  const {responses} = responseData;
  const {workflowId, eventId} = params;
  const dispatch = useDispatch();
  const [query] = useAnalyticsQuery();
  const resolveResponsesQueryFinal = isNps
    ? resolveResponsesQuery(query)
    : resolveResponsesQuery(query, allowedFilters, true);
  const isNewAnalyticsResponseExport = useReleaseFlag(
    'analyticsResponseExportV3',
  );
  const handleCsvExport = () => {
    dispatch(
      startDataExport('data-exports/responses-export', {
        ...resolveResponsesQueryFinal,
        flagged,
        nps_only: isNps,
        workflow_id: workflowId,
        event_id: eventId,
      }),
    );
  };

  return (
    <Container>
      <Header count={responses.length}>
        {isNewAnalyticsResponseExport ? (
          <>
            <Button
              onClick={() => handleCsvExport()}
              type="text"
              className={css.downloadLink}
            >
              {t('Export_Spreadsheet', 'Export Spreadsheet')}
            </Button>
          </>
        ) : (
          <CsvDownloadButton
            className={css.downloadLink}
            url={resolveAnalyticsPath(
              'responses',
              {workflowId, eventId},
              checkResponseLatestApiEnabled(),
            )}
            // @ts-expect-error - TS2322 - Type '{ flagged: boolean; nps_only: boolean; limit?: number; offset?: number; page?: number; edf?: { [key: string]: unknown; }; recipients?: string[]; workflowId?: string; eventId?: string; ... 11 more ...; all_workflows_selected?: boolean; }' is not assignable to type 'AnalyticsQuery'.
            queryResolver={(query) => ({
              ...resolveResponsesQueryFinal,
              flagged,
              nps_only: isNps,
            })}
            apiPath={ANALYTICS_API_V3}
          >
            {t('Export_Spreadsheet', 'Export Spreadsheet')}
          </CsvDownloadButton>
        )}
      </Header>

      {responses.length ? (
        <Responses
          responseData={responseData}
          params={params}
          flagged={flagged}
          flaggedWords={flaggedWords}
          isNps={isNps}
          multiEntityEnabled={multiEntityEnabled}
        />
      ) : (
        <NoData />
      )}

      {responseData.hasMore && (
        <MoreButton onClick={onMoreClick} loadingMore={loadingMore} />
      )}
    </Container>
  );
};

export default PaginatedResponsesView;

// @ts-expect-error - TS2322 - Type 'AbstractComponent<ComponentProps<C>, unknown>' is not assignable to type 'ClassNameComponent'.
export const Container: ClassNameComponent = makeClassNameComponent(
  css.responses,
);

export function Header({
  count,
  children,
}: {
  count: number;
  children?: React.ReactNode;
}) {
  const {t} = useLabelTranslation();
  return (
    <div className={css.count}>
      <strong>{count}</strong>
      <FormattedPlural
        value={count}
        one={t('Person', 'Person')}
        other={t('People', 'People')}
      />
      {children}
    </div>
  );
}

export const DownloadButton: ClassNameComponent<typeof UnstyledButton> =
  makeClassNameComponentCustom(css.downloadLink, UnstyledButton);

export const NoData = () => {
  const {t} = useLabelTranslation();
  return (
    <div className={css.zero}>{t('No_responses_yet', 'No responses yet')}</div>
  );
};

// @ts-expect-error - TS2322 - Type 'AbstractComponent<ComponentProps<C>, unknown>' is not assignable to type 'ClassNameComponent'.
export const ResponsesColumn: ClassNameComponent = makeClassNameComponent(
  css.column,
);

export const Responses = ({
  responseData: {responses = [], events = {}, workflows = {}, modules = {}},
  // @ts-expect-error - TS2739 - Type '{}' is missing the following properties from type 'ResponsesParams': responseId, workflowId, eventId
  params: {workflowId, eventId} = {},
  flagged,
  flaggedWords,
  isNps = false,
  compact = false,
  multiEntityEnabled,
}: {
  responseData: PaginatedResponses;
  params?: ResponsesParams;
  flagged: boolean;
  flaggedWords: FlaggedWord[];
  isNps?: boolean;
  compact?: boolean;
  multiEntityEnabled: boolean;
}): React.ReactElement<React.ComponentProps<'div'>> => {
  const atsEntityMappings = useSelector(selectEntityMappingsByName);
  const showExtra = !eventId;

  return (
    <div className={css.column}>
      {responses
        .filter(
          ({memberResponse}) => memberResponse && memberResponse.length > 0,
        )
        .map((response) => {
          const workflow = workflows[response.workflowId];
          const event = events[response.eventId];
          const id = response.memberResponse[0].eventResponseId;
          const entityMapping = atsEntityMappings[response.recipientEntityType];
          return (
            (!showExtra || event) && (
              <ResponseComponent
                key={id}
                response={response}
                modules={modules}
                showExtra={showExtra}
                workflow={workflow}
                workflowId={(workflow && workflow.id) || workflowId}
                event={event}
                entityMapping={entityMapping}
                isNps={isNps}
                flagged={flagged}
                flaggedWords={flaggedWords}
                compact={compact}
                multiEntityEnabled={multiEntityEnabled}
              />
            )
          );
        })}
    </div>
  );
};

type ResponseComponentProps = {
  response: EventResponse;
  event: Event;
  workflow: Workflow;
  workflowId: string;
  entityMapping?: AtsEntity;
  flagged: boolean;
  flaggedWords: FlaggedWord[];
  modules: {
    [moduleId: string]: RatingScaleQuestion | NPSSurveyQuestion;
  };
  divRef?: () => HTMLElement | null | undefined;
  showExtra: boolean;
  compact: boolean;
  isNps?: boolean;
  selected?: boolean;
  multiEntityEnabled: boolean;
};

export function BaseResponseView({
  header,
  entityMapping,
  entityId,
  entityName,
  externalSourceId,
  isArchivedEntity,
  responseTime,
  isCollapsable,
  event,
  workflow,
  isNps,
  children,
  conversation,
  isJ2,
}: {
  header?: React.ReactNode;
  entityMapping: AtsEntity | null | undefined;
  entityId: string;
  entityName: string;
  externalSourceId?: string | null;
  isArchivedEntity?: boolean;
  responseTime: string | Date;
  isCollapsable?: unknown;
  children: React.ReactNode;
  event?: SenseEvent | null | undefined;
  workflow?: Workflow | null | undefined;
  isNps?: boolean;
  conversation?: Conversation;
  isJ2?: boolean;
}) {
  const {t, makeKey} = useLabelTranslation();
  // TODO (kyle): it's possible we don't want to unnecessarily give every response
  // state.
  const [collapsed, setCollapsed] = React.useState(isCollapsable);
  return (
    <div className={css.response}>
      <div
        className={classify(
          css.headerContainer,
          isCollapsable && css.isCollapsable,
        )}
        // @ts-expect-error - TS2322 - Type '((event: any) => false | void) | null' is not assignable to type 'MouseEventHandler<HTMLDivElement> | undefined'.
        onClick={
          isCollapsable
            ? (event: any) =>
                !(event.target instanceof HTMLAnchorElement) &&
                setCollapsed(!collapsed)
            : null
        }
      >
        {isJ2 && conversation?.j2_workflow ? (
          <Link
            className={css.headerEvent}
            to={(location) => ({
              ...location,
              pathname: `/reports/engagement-deliverability-workflow/workflow/${
                conversation?.j2_workflow || ''
              }/node/task:${conversation?.j2_node || ''}`,
            })}
          >
            {/* @ts-expect-error - TS2322 - Type '{ className: string; nps: boolean | undefined; type: string; }' is not assignable to type 'IntrinsicAttributes & { nps?: any; type?: any; }'. */}
            <EventIcon className={css.headerEventIcon} nps={isNps} type={''} />
            <div className={css.headerEventTitles}>
              <div className={css.headerEventTitle}>
                {conversation?.j2_node_name}
              </div>
              <div className={css.headerWorkflowTitle}>
                {conversation?.j2_workflow_name}
              </div>
            </div>
          </Link>
        ) : null}
        {event && workflow && (
          <Link
            className={css.headerEvent}
            to={(location) => ({
              ...location,
              pathname: `/reports/engagement-deliverability-journeys/journey/${workflow.id}/touchpoint/${event.id}`,
            })}
          >
            <EventIcon
              // @ts-expect-error - TS2322 - Type '{ className: string; nps: boolean | undefined; type: ClientEventType; }' is not assignable to type 'IntrinsicAttributes & { nps?: any; type?: any; }'.
              className={css.headerEventIcon}
              nps={isNps}
              type={event.eventType}
            />
            <div className={css.headerEventTitles}>
              <div className={css.headerEventTitle}>{event.title}</div>
              {workflow && (
                <div className={css.headerWorkflowTitle}>{workflow.name}</div>
              )}
            </div>
          </Link>
        )}
        <EntityHeader
          label={
            entityMapping?.display_name
              ? t(
                  makeKey(entityMapping?.display_name),
                  entityMapping?.display_name,
                )
              : t('Recipient', 'Recipient')
          }
          entityId={entityId}
          entityUrl={entityMapping?.url}
          entityName={entityName}
          externalSourceId={externalSourceId}
          time={responseTime}
          isCollapsable={isCollapsable}
          isCollapsed={collapsed}
          isArchived={isArchivedEntity}
          conversation={conversation || null}
        />
      </div>
      {!collapsed && <div className={css.main}>{children}</div>}
    </div>
  );
}

const ResponseComponent = ({
  response,
  modules,
  showExtra,
  workflowId,
  event,
  workflow,
  entityMapping,
  flagged,
  flaggedWords,
  isNps = false,
}: ResponseComponentProps) => {
  const latestResponseTime = response.memberResponse.reduce(
    (latest, {timeCreated}) => (timeCreated > latest ? timeCreated : latest),
    new Date(0).toISOString(),
  );

  const {recipientEntitySummary} = response;

  return (
    <BaseResponseView
      entityMapping={entityMapping}
      entityId={recipientEntitySummary?.id}
      entityName={recipientEntitySummary?.full_name}
      responseTime={latestResponseTime}
      event={event}
      workflow={workflow}
      isNps={isNps}
    >
      {response.memberResponse.map((answer) => {
        const {id: answerId, moduleId, moduleValue} = answer;
        const isNps = answer.responseModuleType === 'nps_survey_answer';
        const module = modules[moduleId];
        const questionText =
          response?.moduleQuestions?.[answerId] || module?.question || '';

        return (
          <div className={css.answerRow} key={answer.id}>
            <div className={css.question}>{questionText}</div>
            {answer.responseModuleType === 'rating_scale_survey_answer' ||
            isNps ? (
              <RatingScaleAnswer
                moduleValue={Number(answer.moduleValue)}
                maxRating={module.maxRating}
                isNps={isNps}
              />
            ) : flagged ? (
              <Highlighter
                className={css.answer}
                searchWords={flaggedWords.map(
                  (flaggedWord) => flaggedWord.word,
                )}
                highlightClassName={css.flaggedHighlight}
              >
                {/*TODO(marcos) fix autotruncatedtext to work like function component that emits
                     both the full text and truncated text as values? i dunno, this doesn't work:

                     <AutoTruncatedText text={answer.moduleValue} />
                  */}
                {moduleValue instanceof Array
                  ? moduleValue.join(', ')
                  : String(moduleValue)}
              </Highlighter>
            ) : (
              <div className={css.answer}>
                <AutoTruncatedText text={answer.moduleValue} />
              </div>
            )}
          </div>
        );
      })}
    </BaseResponseView>
  );
};

const RatingScaleAnswer = ({
  moduleValue,
  maxRating,
  isNps,
}: {
  moduleValue: number;
  maxRating: number;
  isNps: boolean;
}) => {
  const getMood = isNps ? getNpsRatingMood : getRatingMood;
  const mood = getMood(moduleValue, maxRating);

  return (
    <div className={classify(css.answerRatingScale, css[mood])}>
      {moduleValue} / {maxRating}
    </div>
  );
};

function EntityHeader({
  // @ts-expect-error - TS7031 - Binding element 'label' implicitly has an 'any' type.
  label,
  // @ts-expect-error - TS7031 - Binding element 'entityUrl' implicitly has an 'any' type.
  entityUrl,
  // @ts-expect-error - TS7031 - Binding element 'entityId' implicitly has an 'any' type.
  entityId,
  // @ts-expect-error - TS7031 - Binding element 'entityName' implicitly has an 'any' type.
  entityName,
  // @ts-expect-error - TS7031 - Binding element 'externalSourceId' implicitly has an 'any' type.
  externalSourceId,
  // @ts-expect-error - TS7031 - Binding element 'time' implicitly has an 'any' type.
  time,
  // @ts-expect-error - TS7031 - Binding element 'isCollapsable' implicitly has an 'any' type.
  isCollapsable,
  // @ts-expect-error - TS7031 - Binding element 'isCollapsed' implicitly has an 'any' type.
  isCollapsed,
  // @ts-expect-error - TS7031 - Binding element 'isArchived' implicitly has an 'any' type.
  isArchived,
  // @ts-expect-error - TS7031 - Binding element 'conversation' implicitly has an 'any' type.
  conversation,
}) {
  const {t, language} = useLabelTranslation();
  const uploaded_files = conversation?.uploaded_files || [];

  const handleChildClick = function (e: any) {
    e.stopPropagation();
  };

  return (
    <div className={css.responseContainer}>
      <div className={css.recipientHeader}>
        <div className={css.headerCell}>
          <span className={css.headerLabel}>{label}:</span>
          {isArchived ? (
            <span className={css.archivedName}>{`${t(
              'Archived',
              'Archived',
            )} ${label}`}</span>
          ) : (
            <span className={css.name}>
              {entityUrl ? (
                <Link to={`/people/${entityUrl}/${entityId}`}>
                  {entityName}
                </Link>
              ) : (
                entityName
              )}
            </span>
          )}
        </div>
        <div className={css.headerCell}>
          <span className={css.headerLabel}>{t('Date', 'Date')}:</span>
          <span className={css.date}>
            {formatDateTime(time, language)} {t('UTC', 'UTC')}
          </span>
          {isCollapsable ? (
            // @ts-expect-error - TS2322 - Type 'string | null' is not assignable to type 'string | undefined'.
            <CaretDown className={isCollapsed ? css.flip : null} />
          ) : null}
        </div>
        {externalSourceId ? (
          <div className={css.headerCell}>
            <span className={css.headerLabel}>
              {t('Candidate_ID', 'Candidate ID')}:
            </span>
            <span className={css.date}>{externalSourceId}</span>
          </div>
        ) : null}
      </div>
      {uploaded_files.length > 0 ? (
        <div
          className={classify(
            css.recipientHeader,
            css.responseContainerPadding,
          )}
        >
          <div className={css.filesHeaderCell} onClick={handleChildClick}>
            <span className={css.filesHeaderLabel}>
              {t('Files_uploaded', 'Files uploaded')}:
            </span>
            <div className={css.filesItemContainer}>
              <FileUploadedComps fileUploaded={uploaded_files} />
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

const FileUploadedComps = ({
  fileUploaded,
}: {
  fileUploaded: ConversationUploadedfiles[];
}) => {
  const [collapsed, setCollapsed] = React.useState(false);
  const {t} = useLabelTranslation();

  return (
    <div
      className={classify(
        css.fileHeaderContainer,
        collapsed && css.isCollapsable,
      )}
    >
      {fileUploaded.length > 0 ? (
        <Paragraph className={css.fileLinkable}>
          <a href={createDownloadableUrl(fileUploaded[0])} target="_blank">
            {fileUploaded[0].file_name}
          </a>
          {!collapsed && (
            <CaretDown
              className={css.iconLinkable}
              onClick={() => setCollapsed(!collapsed)}
            />
          )}
        </Paragraph>
      ) : null}
      {collapsed && fileUploaded.length > 1 && (
        <div className={css.filesMain}>
          {fileUploaded.map(
            (file, index) =>
              index !== 0 && (
                <a href={createDownloadableUrl(file)} target="_blank">
                  <Paragraph className={css.fileLinkable}>
                    {file.file_name}
                  </Paragraph>
                </a>
              ),
          )}

          <Paragraph
            className={css.fileLinkable}
            onClick={() => setCollapsed(!collapsed)}
          >
            {t('Collapse', 'Collapse')}
            <CaretDown className={classify(css.iconLinkable, css.flip)} />
          </Paragraph>
        </div>
      )}
    </div>
  );
};

const createDownloadableUrl = (files: ConversationUploadedfiles): string =>
  `${window.location.origin}/api/v1/file-access/download?file_container=${
    files.file_container
  }&file_path=${files.file_path}&file_name=${encodeURIComponent(
    files.file_name,
  )}`;
